/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Download, Facebook, ImageIcon, Instagram, Loader2, MoveHorizontal, RefreshCw, RotateCcw, Send } from "lucide-react";
import { api } from "@/lib/api";
import PhotoStage, { useImageAspect } from "@/components/PhotoStage";
import { FIT_WIDTH, MAX_ZOOM, MIN_ZOOM, rectToPlacement } from "@/lib/carouselLayout";
import {
  CARD_SIZES, cardPhotoBox, cardPhotoRect, isManual, placedPhoto, placementKey,
} from "@/lib/storyCardLayout";
import { ScheduleControls } from "@/components/ScheduledPostsList";
import { formatInTz, propertyTimezone } from "@/lib/timezone";

// Preview size on-screen (fits the article page). Content renders at full
// resolution; we scale visually via CSS transform.
const PREVIEW_W = 240;
const PREVIEW_ALT_W = 220;
// The card that isn't being previewed: still rendered (it is captured
// for download / publishing) but out of sight and out of the layout.
const CARD_PARKED = {
  position: "absolute",
  left: -100000,
  top: 0,
  pointerEvents: "none",
};

const DEFAULT_PAD = { top: 96, right: 96, bottom: 96, left: 96 };

const TEMPLATES = [
  { id: "spotlight", label: "Spotlight" },
  { id: "split",     label: "Split" },
  { id: "frame",     label: "Frame" },
];

/**
 * Fetch a remote image URL and expose it as a base64 data URL.
 *
 * Why we need this: on mobile Safari (and iOS embedded WebViews), when
 * `html-to-image` walks the DOM to build the export canvas it draws
 * <img> elements onto the canvas. If those images came from a different
 * origin *without* a CORS-friendly response, the canvas becomes
 * "tainted" and either throws or emits an image with the tainted layers
 * skipped — which is why editors on iPhones were downloading cards
 * with no background. Desktop Chrome/Firefox happen to be more lenient
 * or have the image already cached with CORS metadata, so it worked
 * there.
 *
 * Data URLs bypass CORS entirely because there's no fetch — the pixels
 * are inline. We fetch once, convert to base64, and hand that to the
 * templates as the `src`. On any fetch failure we fall back to the
 * original URL so at least desktop keeps working.
 */
function useImageAsDataUrl(url) {
  const [dataUrl, setDataUrl] = useState(url || "");
  useEffect(() => {
    if (!url) {
      setDataUrl("");
      return;
    }
    let cancelled = false;
    // Same-origin data URLs (already inlined) don't need re-fetching.
    if (url.startsWith("data:")) {
      setDataUrl(url);
      return;
    }
    (async () => {
      try {
        const r = await fetch(url, { cache: "force-cache", mode: "cors" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (!cancelled) setDataUrl(reader.result);
        };
        reader.onerror = () => {
          if (!cancelled) setDataUrl(url);  // fallback to raw URL
        };
        reader.readAsDataURL(blob);
      } catch {
        // Silent fallback — desktop still works with the original URL.
        if (!cancelled) setDataUrl(url);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);
  return dataUrl;
}

function pickOne(list, seedKey) {
  if (!list || list.length === 0) return "";
  // Deterministic per-article so refresh doesn't shuffle every time —
  // seed on the article id + a nonce so the Shuffle button rotates.
  let h = 0;
  for (const ch of String(seedKey)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return list[Math.abs(h) % list.length];
}

/**
 * Reliably capture a rendered card node to a PNG data URL.
 *
 * Problem this solves: `html-to-image` walks the DOM synchronously, but
 * <img> elements paint asynchronously — the browser only has pixel data
 * once the image has been decoded off-thread. `img.complete === true`
 * (which is what the library checks) is a lie for freshly-set data
 * URLs: `.complete` flips to `true` the moment the src attribute is
 * assigned, well before the pixels are actually ready. When we hit
 * capture in that window, `html-to-image` emits a canvas with the
 * <img> missing and the dark `bg-neutral-900` container shows through —
 * which is exactly the "black background" symptom the editor reported.
 *
 * We fix this with two belt-and-braces mitigations:
 *   1) Explicitly `await img.decode()` on every <img> inside the node.
 *      Modern browsers guarantee this promise only resolves once the
 *      image is actually paintable (unlike `.complete`).
 *   2) Run `toPng` TWICE, discarding the first result. This is the
 *      documented workaround for a known html-to-image race (see
 *      github.com/bubkoo/html-to-image #361, #490) where the first
 *      snapshot after new images are loaded sometimes drops them
 *      because the internal canvas hasn't had a chance to warm the
 *      browser's image cache. The second call always sees the fully
 *      decoded images.
 *
 * The result is deterministic exports across Chrome, Safari, mobile,
 * and inside embedded WebViews — no more "sometimes the background is
 * there, sometimes it's black" for downloads or social shares.
 */
async function captureCardPng(node, w, h) {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      // decode() is unsupported on very old browsers — fall back to
      // waiting on the load event, then a rAF for a paint pass.
      if (typeof img.decode === "function") {
        try {
          await img.decode();
        } catch {
          /* decoded images sometimes reject on transparent PNGs — safe to ignore */
        }
      } else if (!img.complete) {
        await new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
        });
      }
    }),
  );
  // Give the browser one paint cycle so any freshly-decoded images
  // land on-screen before we walk the DOM.
  await new Promise((res) => requestAnimationFrame(() => res()));
  const opts = {
    cacheBust: true,
    pixelRatio: 1,
    width: w,
    height: h,
    style: {
      transform: "scale(1)",
      transformOrigin: "top left",
    },
  };
  // Warm-up pass: the FIRST call often drops just-loaded images on
  // Safari. We deliberately discard the result and re-capture.
  try {
    await toPng(node, opts);
  } catch {
    /* warm-up failure is fine; the real capture is next */
  }
  return await toPng(node, opts);
}

/**
 * Save a PNG data URL to disk from the browser.
 *
 * Why this isn't a one-liner: `<a download="…" href="data:…">.click()`
 * looks universal but **only works on desktop Chromium/Firefox**.
 *   • iOS Safari: has never honoured the `download` attribute — the
 *     click either opens the image inline (best case) or does nothing
 *     (in the Facebook / Instagram / Gmail in-app WebViews, which is
 *     exactly where our editors live on their phones).
 *   • Android Chrome: usually works, but some in-app browsers strip
 *     the attribute too.
 *
 * The modern universal solution is the Web Share API with a `File`
 * payload. When the browser supports `navigator.canShare({ files })`
 * we hand off to the OS share sheet — the user picks "Save Image",
 * "Save to Files", AirDrop, or share to any app. That's the exact
 * UX editors expect on mobile and it survives in-app browsers.
 *
 * Fallback order:
 *   1. Web Share API with File  → mobile share sheet
 *   2. `<a download>`            → desktop
 *   3. Open in new tab           → last resort: user long-presses to save
 */
async function downloadOrShare(dataUrl, filename) {
  // 1. Try Web Share API with a File (best mobile UX by far)
  try {
    if (typeof navigator !== "undefined" && navigator.canShare) {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return { ok: true, via: "share" };
      }
    }
  } catch (err) {
    // User cancelled the share sheet — that's not a failure, don't
    // fall through to the anchor click (which would silently
    // download the file twice on desktops that support both).
    if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
      return { ok: true, via: "cancelled" };
    }
    // Any other share failure → fall through to anchor download
  }

  // 2. Anchor download — works on desktop Chromium / Firefox / Edge
  try {
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataUrl;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { ok: true, via: "anchor" };
  } catch {
    /* fall through */
  }

  // 3. Last resort — open the image in a new tab; the user can
  //    long-press or right-click to save. This is what iOS Safari
  //    editors hit when the share sheet isn't available (rare — only
  //    on very old iOS versions or exotic in-app browsers).
  const w = window.open();
  if (w) {
    w.document.title = filename;
    w.document.body.innerHTML =
      `<img src="${dataUrl}" alt="${filename}" ` +
      `style="max-width:100%;height:auto;display:block;margin:auto;">`;
    return { ok: true, via: "newtab" };
  }
  return { ok: false, via: "none" };
}

function hostFromUrl(url) {
  if (!url) return "";
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return String(url).replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

// --------- The three templates -----------------------------------------

/**
 * Small disclosure caption drawn at the bottom-left of the card. Sits
 * ABOVE all other card content (highest absolute z-index via source
 * order) and outside the safe content padding so it doesn't compete
 * with "Read more" / site attribution rows. Sized in px so
 * html-to-image captures it identically to what's on screen.
 */
function LegendOverlay({ text, hScale = 1 }) {
  if (!text) return null;
  // Scale factor is +15% over the original design constants — matches
  // the same bump applied to the backend Pillow bake path so both
  // surfaces (baked WP featured image and the CSS card) keep visual
  // parity. Anchor offsets (bottom/left) intentionally stay at their
  // original values so the pill sits in the same corner spot; only the
  // pill itself grows.
  const s = (v) => Math.round(v * hScale * 1.15);
  return (
    <div
      className="absolute font-mono uppercase tracking-widest"
      style={{
        bottom: Math.round(24 * hScale),
        left: Math.round(24 * hScale),
        padding: `${s(8)}px ${s(14)}px`,
        backgroundColor: "rgba(0,0,0,0.7)",
        color: "#fff",
        fontSize: s(18),
        letterSpacing: s(2),
        maxWidth: `calc(100% - ${Math.round(48 * hScale)}px)`,
        lineHeight: 1.2,
        borderRadius: s(6),
      }}
    >
      {text}
    </div>
  );
}

// Scaling helper for template pixel values. All templates were designed
// against a 1080×1920 (9:16 Instagram Story) canvas. When rendered at
// the 4:5 (1080×1350) feed size, the vertical axis shrinks ~30% so fixed
// pixel offsets and font-sizes started to overlap. Multiplying every
// hardcoded px value by `h/1920` keeps the layout proportionally
// identical at any height.
const s1920 = (v, h) => Math.round(v * (h / 1920));

/** The card photo sized by hand (Photo position editor): absolutely
 *  placed at `rect`, relative to its photo box (lib/storyCardLayout.js).
 *  Without `rect` the template keeps its automatic object-cover <img>. */
function PlacedPhoto({ src, rect }) {
  return (
    <img
      src={src}
      alt=""
      crossOrigin="anonymous"
      style={{
        position: "absolute", left: rect.left, top: rect.top, width: rect.width,
        height: rect.height == null ? "auto" : rect.height,
        maxWidth: "none", maxHeight: "none",
      }}
    />
  );
}

function Spotlight({ bg, logo, chip, tag, headline, site, pad, legend, h = 1920, photo = null }) {
  const s = (v) => s1920(v, h);
  return (
    <div className="w-full h-full relative overflow-hidden bg-neutral-900">
      {bg && photo && <PlacedPhoto src={bg} rect={photo} />}
      {bg && !photo && (
        <img
          src={bg}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 55%, rgba(0,0,0,.75) 100%)",
        }}
      />
      {/* Top chip */}
      {chip && (
        <div
          className="absolute text-white font-black uppercase tracking-widest"
          style={{
            top: pad.top, left: pad.left,
            padding: `${s(12)}px ${s(24)}px`,
            backgroundColor: "#DC2626",
            fontSize: s(34),
            borderRadius: s(8),
            letterSpacing: s(4),
          }}
        >
          {chip}
        </div>
      )}
      {/* Logo */}
      {logo && (
        <img
          src={logo}
          alt="logo"
          crossOrigin="anonymous"
          className="absolute"
          style={{
            top: pad.top, right: pad.right,
            maxWidth: s(340), maxHeight: s(180), objectFit: "contain",
          }}
        />
      )}
      {/* Content */}
      <div className="absolute" style={{ left: pad.left, right: pad.right, bottom: pad.bottom + s(140) }}>
        {tag && (
          <div
            className="text-white font-black uppercase tracking-widest"
            style={{ fontSize: s(48), letterSpacing: s(6), marginBottom: s(16) }}
          >
            {tag}
          </div>
        )}
        <div
          className="font-black uppercase leading-[1.05]"
          style={{ fontSize: s(78), letterSpacing: -1 }}
        >
          {headline.split(" ").reduce((rows, word) => {
            const line = rows[rows.length - 1] || "";
            if ((line + " " + word).trim().length > 22) {
              rows.push(word);
            } else {
              rows[rows.length - 1] = line ? line + " " + word : word;
            }
            return rows;
          }, [""]).map((line, i) => (
            <div key={i} style={{ lineHeight: 1.05, marginBottom: s(4) }}>
              <span
                style={{
                  backgroundColor: "#D9F65C",
                  color: "#000",
                  padding: `${s(6)}px ${s(14)}px`,
                  boxDecorationBreak: "clone",
                  WebkitBoxDecorationBreak: "clone",
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div
        className="absolute text-white uppercase tracking-widest font-mono"
        style={{ bottom: pad.bottom + s(80), left: pad.left, right: pad.right, fontSize: s(26), letterSpacing: s(3) }}
      >
        SAVE THIS. SHARE IT.
      </div>
      <div
        className="absolute flex items-end justify-between text-white font-mono"
        style={{ bottom: pad.bottom, left: pad.left, right: pad.right, fontSize: s(32) }}
      >
        <span style={{ color: "#D9F65C" }}>↘ Read more</span>
        <span>{site}</span>
      </div>
      <LegendOverlay text={legend} hScale={h / 1920} />
    </div>
  );
}

function Split({ bg, logo, chip, tag, headline, site, pad, legend, h = 1920, photo = null }) {
  const s = (v) => s1920(v, h);
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: "#0B0B0B" }}>
      <div className="absolute inset-x-0 top-0 h-[62%] overflow-hidden">
        {bg && photo && <PlacedPhoto src={bg} rect={photo} />}
        {bg && !photo && (
          <img
            src={bg}
            alt=""
            crossOrigin="anonymous"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {chip && (
          <div
            className="absolute text-white font-black uppercase tracking-widest"
            style={{
              top: pad.top, left: pad.left,
              padding: `${s(12)}px ${s(24)}px`,
              backgroundColor: "#DC2626",
              fontSize: s(34), borderRadius: s(8), letterSpacing: s(4),
            }}
          >
            {chip}
          </div>
        )}
        {logo && (
          <img
            src={logo}
            alt="logo"
            crossOrigin="anonymous"
            className="absolute"
            style={{ top: pad.top, right: pad.right, maxWidth: s(340), maxHeight: s(180), objectFit: "contain" }}
          />
        )}
      </div>
      <div
        className="absolute inset-x-0 bottom-0 h-[38%]"
        style={{ paddingLeft: pad.left, paddingRight: pad.right, paddingTop: s(60), paddingBottom: pad.bottom }}
      >
        {tag && (
          <div className="uppercase tracking-widest font-black" style={{ color: "#D9F65C", fontSize: s(40), letterSpacing: s(5), marginBottom: s(20) }}>
            {tag}
          </div>
        )}
        <div className="text-white font-black uppercase leading-tight" style={{ fontSize: s(70), letterSpacing: -1 }}>
          {headline}
        </div>
        <div className="absolute flex items-center justify-between font-mono" style={{ left: pad.left, right: pad.right, bottom: pad.bottom + s(60), fontSize: s(26), letterSpacing: s(3), color: "rgba(255,255,255,0.8)" }}>
          <span>SAVE THIS. SHARE IT.</span>
        </div>
        <div className="absolute flex items-center justify-between font-mono" style={{ left: pad.left, right: pad.right, bottom: pad.bottom, fontSize: s(30) }}>
          <span style={{ color: "#D9F65C" }}>↘ Read more</span>
          <span className="text-white">{site}</span>
        </div>
      </div>
      <LegendOverlay text={legend} hScale={h / 1920} />
    </div>
  );
}

function Frame({ bg, logo, chip, tag, headline, site, pad, legend, h = 1920, photo = null }) {
  const s = (v) => s1920(v, h);
  const framePadX = pad.left + s(40);
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: "#111827" }}>
      {bg && (
        <img
          src={bg}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm scale-110"
        />
      )}
      <div
        className="absolute"
        style={{
          top: pad.top + s(240),
          left: framePadX,
          right: framePadX,
          height: s(780),
          border: `${s(18)}px solid #D9F65C`,
          overflow: "hidden",
        }}
      >
        {bg && photo && <PlacedPhoto src={bg} rect={photo} />}
        {bg && !photo && (
          <img
            src={bg}
            alt=""
            crossOrigin="anonymous"
            className="w-full h-full object-cover"
          />
        )}
      </div>
      {chip && (
        <div
          className="absolute text-white font-black uppercase tracking-widest"
          style={{
            top: pad.top, left: pad.left,
            padding: `${s(12)}px ${s(24)}px`,
            backgroundColor: "#DC2626",
            fontSize: s(34), borderRadius: s(8), letterSpacing: s(4),
          }}
        >
          {chip}
        </div>
      )}
      {logo && (
        <img
          src={logo}
          alt="logo"
          crossOrigin="anonymous"
          className="absolute"
          style={{ top: pad.top, right: pad.right, maxWidth: s(320), maxHeight: s(160), objectFit: "contain" }}
        />
      )}
      <div className="absolute" style={{ left: pad.left, right: pad.right, bottom: pad.bottom + s(140) }}>
        {tag && (
          <div className="uppercase tracking-widest font-black" style={{ color: "#D9F65C", fontSize: s(42), letterSpacing: s(5), marginBottom: s(16) }}>
            {tag}
          </div>
        )}
        <div className="text-white font-black uppercase leading-tight" style={{ fontSize: s(66), letterSpacing: -1 }}>
          {headline}
        </div>
      </div>
      <div className="absolute text-white/70 uppercase tracking-widest font-mono text-center" style={{ left: pad.left, right: pad.right, bottom: pad.bottom + s(80), fontSize: s(26), letterSpacing: s(3) }}>
        SAVE THIS. SHARE IT.
      </div>
      <div
        className="absolute flex items-center justify-between font-mono"
        style={{ left: pad.left, right: pad.right, bottom: pad.bottom, fontSize: s(32) }}
      >
        <span style={{ color: "#D9F65C" }}>↘ Read more</span>
        <span className="text-white">{site}</span>
      </div>
      <LegendOverlay text={legend} hScale={h / 1920} />
    </div>
  );
}

// ---------------- Main panel -------------------------------------------

export default function StoryCardPanel({ article, settings }) {
  const [templateId, setTemplateId] = useState("spotlight");
  const [useGeneric, setUseGeneric] = useState(false);
  const [selectedBgId, setSelectedBgId] = useState(null);
  const [headline, setHeadline] = useState(article.headline || "");
  const [shuffleNonce, setShuffleNonce] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAlt, setDownloadingAlt] = useState(false);
  const [shareIgPageId, setShareIgPageId] = useState("");
  const [shareIgCaption, setShareIgCaption] = useState("");
  // Follow the article's Instagram copy when it changes (e.g. an
  // accepted fact-check fix) if the caption still holds the old copy
  // untouched. Empty captions are seeded when the share form opens.
  const seededIgCaptionRef = useRef(article.instagram_post || "");
  useEffect(() => {
    const next = article.instagram_post || "";
    setShareIgCaption((cur) =>
      cur && cur === seededIgCaptionRef.current ? next : cur,
    );
    seededIgCaptionRef.current = next;
  }, [article.instagram_post]);
  const [shareIgOpen, setShareIgOpen] = useState(false);
  // Post type for the IG share flow: "feed" = 4:5 Feed post (default),
  // "story" = 9:16 Instagram Story (uses media_type=STORIES, no caption).
  const [shareIgTarget, setShareIgTarget] = useState("feed");
  const [igScheduleEnabled, setIgScheduleEnabled] = useState(false);
  const [igScheduledFor, setIgScheduledFor] = useState("");
  // Facebook Story publishing (9:16 card → /photo_stories).
  const [shareFbStoryPageId, setShareFbStoryPageId] = useState("");
  const [shareFbStoryOpen, setShareFbStoryOpen] = useState(false);
  const [fbStoryScheduleEnabled, setFbStoryScheduleEnabled] = useState(false);
  const [fbStoryScheduledFor, setFbStoryScheduledFor] = useState("");

  // Cascade delay + WP-schedule detection must both be declared
  // BEFORE the useEffect that consumes them — JS hoists `const`
  // bindings into a temporal dead zone, so referencing them earlier
  // in the same scope crashes the render (blank story-card panel).
  const cascadeDelayMin =
    Number.isFinite(Number(settings?.social_cascade_delay_minutes))
      ? Number(settings.social_cascade_delay_minutes)
      : 15;
  const wpScheduledAt = article.wordpress_scheduled_date || "";
  const wpScheduledInFuture =
    !!wpScheduledAt && new Date(wpScheduledAt).getTime() > Date.now();

  // When the article's WordPress publish is scheduled for the future,
  // pre-fill the IG + FB Story schedule fields to N minutes AFTER
  // the WP publish (N = cascadeDelayMin, default 15). Values are
  // stored as UTC ISO strings — `ScheduleControls` handles the
  // wall-clock ↔ UTC conversion internally, so anything the parent
  // owns must ALWAYS be a proper UTC ISO. Otherwise the submit path
  // double-converts and produces `""`, which the backend interprets
  // as "publish immediately" (see git blame — this bit us badly).
  // Bumped after each successful SCHEDULED send. Properties with more
  // than one Page publish the same card to each in turn, and every one
  // has to inherit the cascade. `onSuccess` used to clear the toggle,
  // so the second Page onward silently fell back to publish-now.
  const [cascadeArm, setCascadeArm] = useState(0);
  // Last value the cascade auto-filled. While a field still holds
  // exactly that, the editor hasn't touched it and we may overwrite —
  // which matters because `settings` (and with it the property's real
  // cascade delay) resolves AFTER the first render, so the first
  // auto-fill uses the 15-minute fallback. A different value means the
  // editor typed their own time; leave it alone.
  const lastAutoIsoRef = useRef("");
  useEffect(() => {
    if (!wpScheduledInFuture) return;
    const t = new Date(wpScheduledAt).getTime() + cascadeDelayMin * 60 * 1000;
    const iso = new Date(t).toISOString();
    const prevAuto = lastAutoIsoRef.current;
    lastAutoIsoRef.current = iso;
    const keepOrFill = (prev) => (!prev || prev === prevAuto ? iso : prev);
    setIgScheduledFor(keepOrFill);
    setIgScheduleEnabled(true);
    setFbStoryScheduledFor(keepOrFill);
    setFbStoryScheduleEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wpScheduledAt, article.id, cascadeDelayMin, cascadeArm]);

  const cardRef = useRef(null);
  const cardAltRef = useRef(null);
  const qc = useQueryClient();

  const { data: connData } = useQuery({
    queryKey: ["social-connections"],
    queryFn: api.listSocialConnections,
  });
  const igPages = useMemo(
    () =>
      (connData?.items || []).filter(
        (p) => p.has_ig && !p.hidden,
      ),
    [connData],
  );
  // All non-hidden connected FB pages — FB Stories don't require a
  // linked Instagram account, just page_manage_posts on the Page itself.
  const fbPages = useMemo(
    () => (connData?.items || []).filter((p) => !p.hidden),
    [connData],
  );

  useEffect(() => {
    setHeadline(article.headline || "");
  }, [article.headline]);

  const backgrounds = settings?.card_backgrounds || [];
  useEffect(() => {
    if (useGeneric && !selectedBgId && backgrounds.length > 0) {
      setSelectedBgId(backgrounds[0].id);
    }
  }, [useGeneric, selectedBgId, backgrounds]);

  // Resolve the actual background URL to use.
  const bg = useMemo(() => {
    if (useGeneric) {
      const picked = backgrounds.find((b) => b.id === selectedBgId);
      return picked ? picked.url : null;
    }
    return (
      article.generated_image_url ||
      article.source_images?.[0] ||
      null
    );
  }, [useGeneric, selectedBgId, backgrounds, article]);

  const chip = useMemo(
    () => pickOne(settings?.card_top_chips, article.id + ":c:" + shuffleNonce),
    [settings, article.id, shuffleNonce]
  );
  const tag = useMemo(
    () => pickOne(settings?.card_headline_tags, article.id + ":t:" + shuffleNonce),
    [settings, article.id, shuffleNonce]
  );
  const site = hostFromUrl(settings?.wp_url) || "your site";
  const logo = settings?.card_logo_url || null;

  // Convert both bg + logo to base64 data URLs *before* rendering so
  // html-to-image captures them reliably on mobile Safari (see
  // `useImageAsDataUrl` docblock). Small ~1-2s delay on first paint,
  // no delay after browser cache warms up.
  const bgReady = useImageAsDataUrl(bg);
  const logoReady = useImageAsDataUrl(logo);
  // "Ready" = every non-empty source has been converted. If it's still
  // a raw http(s) URL, the conversion is in-flight and downloading now
  // would produce a card with a missing background on mobile Safari.
  const imagesReady =
    (!bg || (bgReady || "").startsWith("data:")) &&
    (!logo || (logoReady || "").startsWith("data:"));

  // Photo position (drag / resize the photo inside its box), kept per
  // template per card size — see lib/storyCardLayout.js. {} = every card
  // automatic (object-cover, the old look). A different photo starts
  // over, since a placement only makes sense for the photo it was made on.
  const [placements, setPlacements] = useState({});
  const [placeSize, setPlaceSize] = useState("feed");
  useEffect(() => {
    setPlacements({});
  }, [bg]);
  const bgAspect = useImageAspect(bgReady || "");
  const setPlacement = (size, p) =>
    setPlacements((m) => ({ ...m, [placementKey(templateId, size)]: p }));

  // Per-property size + padding (fallback to Instagram Story defaults).
  const cardW = settings?.card_size?.width || 1080;
  const cardH = settings?.card_size?.height || 1920;
  // Secondary size (Instagram feed 4:5 post) — falls back to 1080×1350.
  const cardAltW = settings?.card_size_alt?.width || 1080;
  const cardAltH = settings?.card_size_alt?.height || 1350;
  const pad = {
    top: settings?.card_padding?.top ?? DEFAULT_PAD.top,
    right: settings?.card_padding?.right ?? DEFAULT_PAD.right,
    bottom: settings?.card_padding?.bottom ?? DEFAULT_PAD.bottom,
    left: settings?.card_padding?.left ?? DEFAULT_PAD.left,
  };

  // Same resolution logic as ArticlePage's ArticleImages + backend's
  // resolve_legend_text — keeps the card's baked overlay in sync with
  // the article-page preview and the WP-uploaded featured image.
  const legendText = (() => {
    const key = article.image_legend || "";
    if (!key) return "";
    const text = (article.image_legend_custom_text || "").trim();
    if (key === "custom") return text;
    // Mirrors backend COURTESY_PREFIX — empty name renders nothing
    // rather than a dangling prefix.
    if (key === "courtesy") return text ? `Photo courtesy of ${text}` : "";
    if (key === "ai_generated") return "AI-generated illustration";
    if (key === "stock_photo") return "Stock photo, for illustrative purposes";
    return "";
  })();

  const storyBox = cardPhotoBox(templateId, { w: cardW, h: cardH, pad });
  const storyPlacement = placements[placementKey(templateId, "story")];
  const cardProps = {
    bg: bgReady, logo: logoReady, chip, tag, headline: headline.toUpperCase(), site, pad, legend: legendText, h: cardH,
    photo: placedPhoto(storyPlacement, storyBox, bgAspect),
  };
  // Alt-size (feed 4:5) uses a proportionally-scaled pad so the same
  // template design stays balanced at the shorter canvas.
  const altScale = cardAltH / cardH;
  const padAlt = {
    top: Math.round(pad.top * altScale),
    right: Math.round(pad.right * altScale),
    bottom: Math.round(pad.bottom * altScale),
    left: Math.round(pad.left * altScale),
  };
  const feedBox = cardPhotoBox(templateId, { w: cardAltW, h: cardAltH, pad: padAlt });
  const feedPlacement = placements[placementKey(templateId, "feed")];
  const cardPropsAlt = {
    ...cardProps, pad: padAlt, h: cardAltH,
    photo: placedPhoto(feedPlacement, feedBox, bgAspect),
  };
  const Template =
    templateId === "spotlight" ? Spotlight :
    templateId === "split"     ? Split :
                                 Frame;

  const download = async () => {
    if (!cardRef.current) return;
    if (!imagesReady) {
      toast.message("Preparing images… (this only happens once)");
      return;
    }
    setDownloading(true);
    try {
      await renderAndDownload(cardRef.current, cardW, cardH, "story");
    } catch (e) {
      console.error(e);
      toast.error(
        "Could not render card. If the background image is on a different domain, it may block export — try a Generic background.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const downloadAlt = async () => {
    if (!cardAltRef.current) return;
    if (!imagesReady) {
      toast.message("Preparing images… (this only happens once)");
      return;
    }
    setDownloadingAlt(true);
    try {
      await renderAndDownload(cardAltRef.current, cardAltW, cardAltH, "post");
    } catch (e) {
      console.error(e);
      toast.error(
        "Could not render card. If the background image is on a different domain, it may block export — try a Generic background.",
      );
    } finally {
      setDownloadingAlt(false);
    }
  };

  const renderAndDownload = async (node, w, h, suffix) => {
    const dataUrl = await captureCardPng(node, w, h);
    const slug =
      (article.headline || "story")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40);
    const filename = `card-${templateId}-${suffix}-${slug || "story"}.png`;
    const res = await downloadOrShare(dataUrl, filename);
    if (!res.ok) {
      throw new Error(
        "Your browser blocked the download. Try again from the browser's own tab (not an in-app view).",
      );
    }
  };

  // Instagram publishing: choose which card ref to render based on
  // `shareIgTarget`. Feed (4:5) uses the alt card ref because IG Feed
  // only accepts aspect ratios between 1.91:1 and 4:5 — the primary
  // 9:16 gets rejected with "Only photo or video can be accepted".
  // Story uses the primary 9:16 ref (media_type=STORIES) which is
  // what Meta expects for Stories.
  const shareToIg = useMutation({
    mutationFn: async () => {
      const targetIsStory = shareIgTarget === "story";
      const ref = targetIsStory ? cardRef : cardAltRef;
      const w = targetIsStory ? cardW : cardAltW;
      const h = targetIsStory ? cardH : cardAltH;
      if (!ref.current)
        throw new Error("Story card not ready — try again in a moment.");
      if (!imagesReady)
        throw new Error("Card images are still loading — try again in a second.");
      const dataUrl = await captureCardPng(ref.current, w, h);
      return api.shareToInstagram(article.id, {
        page_id: shareIgPageId,
        // Stories don't render text captions — Meta ignores the field
        // for STORIES containers — but we still pass it so the request
        // shape stays consistent (and it's saved with any schedule row).
        caption: shareIgCaption,
        card_png_base64: dataUrl,
        target: shareIgTarget,
        // Already a UTC ISO — `ScheduleControls` did the wall-clock
        // conversion. Do NOT wrap again (would silently return "" and
        // publish immediately, see git blame).
        scheduled_for: igScheduleEnabled ? igScheduledFor : undefined,
      });
    },
    onSuccess: (r) => {
      if (r.scheduled) {
        toast.success(
          `Scheduled Instagram post for ${formatInTz(
            r.post.scheduled_for,
            propertyTimezone(settings),
          )}`,
        );
        // Re-arm rather than clear — see `cascadeArm` above.
        if (wpScheduledInFuture) setCascadeArm((n) => n + 1);
        else setIgScheduleEnabled(false);
        qc.invalidateQueries({ queryKey: ["scheduled-posts", article.id] });
      } else if (r.publishing) {
        // The Meta calls run in the background — no ig_media_id yet.
        // The article doc will get `social_posts` + `ig_publish_status`
        // updated when the background task finishes. Refetch a few
        // times so the UI picks that up automatically.
        toast.success(
          "Publishing to Instagram in the background — usually done in 10–30 s.",
        );
        [4000, 10000, 20000, 40000].forEach((delay) => {
          setTimeout(() => {
            qc.invalidateQueries({ queryKey: ["article", article.id] });
            qc.invalidateQueries({ queryKey: ["articles"] });
          }, delay);
        });
      } else {
        toast.success(`Posted to Instagram — media id ${r.ig_media_id}`);
        qc.invalidateQueries({ queryKey: ["article", article.id] });
        qc.invalidateQueries({ queryKey: ["articles"] });
      }
      setShareIgOpen(false);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Instagram publish failed",
      ),
  });

  // Default the Instagram picker & caption the first time we open it.
  useEffect(() => {
    if (shareIgOpen) {
      if (!shareIgPageId) {
        // Honour the property's pinned default Page when it has an IG
        // account attached; otherwise fall back to the first IG-capable
        // Page (the old behaviour).
        const pinned = igPages.find(
          (p) => p.page_id === connData?.default_page_id,
        );
        const pick = pinned?.page_id || igPages[0]?.page_id;
        if (pick) setShareIgPageId(pick);
      }
      if (!shareIgCaption && article.instagram_post) {
        setShareIgCaption(article.instagram_post);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareIgOpen, connData]);

  // Facebook Story publishing: 9:16 card only. FB /photo_stories accepts
  // a photo_id obtained by first uploading with published=false, then
  // wrapping in a Story. Stories expire 24h after publish and don't
  // support captions — only the image goes live.
  const shareToFbStory = useMutation({
    mutationFn: async () => {
      if (!cardRef.current)
        throw new Error("Story card not ready — try again in a moment.");
      if (!imagesReady)
        throw new Error("Card images are still loading — try again in a second.");
      const dataUrl = await captureCardPng(cardRef.current, cardW, cardH);
      return api.shareToFacebookStory(article.id, {
        page_id: shareFbStoryPageId,
        card_png_base64: dataUrl,
        // Same story as IG above — already a UTC ISO from
        // `ScheduleControls`, send straight through.
        scheduled_for: fbStoryScheduleEnabled ? fbStoryScheduledFor : undefined,
      });
    },
    onSuccess: (r) => {
      if (r.scheduled) {
        toast.success(
          `Scheduled Facebook Story for ${formatInTz(
            r.post.scheduled_for,
            propertyTimezone(settings),
          )}`,
        );
        // Re-arm rather than clear — see `cascadeArm` above.
        if (wpScheduledInFuture) setCascadeArm((n) => n + 1);
        else setFbStoryScheduleEnabled(false);
        qc.invalidateQueries({ queryKey: ["scheduled-posts", article.id] });
        qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      } else {
        toast.success(
          r.permalink
            ? "Facebook Story published — it will be live for 24 hours."
            : "Facebook Story request accepted by Meta.",
        );
        qc.invalidateQueries({ queryKey: ["article", article.id] });
        qc.invalidateQueries({ queryKey: ["articles"] });
      }
      setShareFbStoryOpen(false);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Facebook Story publish failed",
      ),
  });

  useEffect(() => {
    if (shareFbStoryOpen && !shareFbStoryPageId) {
      // Same rule as the IG picker: the property's pinned default Page
      // first, then Meta's list order.
      const pinned = fbPages.find(
        (p) => p.page_id === connData?.default_page_id,
      );
      const pick = pinned?.page_id || fbPages[0]?.page_id;
      if (pick) setShareFbStoryPageId(pick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareFbStoryOpen, connData]);

  return (
    <section className="mb-8 bg-white border border-[var(--border)]" data-testid="story-card-panel">
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-[var(--accent)]" />
          <span className="overline">Story card · Instagram / Facebook</span>
        </div>
      </div>

      <div className="p-5 grid gap-6 md:grid-cols-[auto_1fr]">
        {/* Preview — ONE card at a time (Feed 4:5 by default; the
            "Size" buttons switch to Story 9:16). The other card stays
            mounted but parked off-screen: Download, Share and the
            scheduled posts all capture it from its ref, so it must
            keep rendering even while it isn't shown. */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative flex flex-wrap gap-4 items-start justify-center">
            <div
              className="flex flex-col items-center gap-1"
              data-testid="story-card-preview-story"
              aria-hidden={placeSize !== "story"}
              style={placeSize === "story" ? undefined : CARD_PARKED}
            >
              <div className="overline text-[10px]">Story · 9:16</div>
              <div
                style={{
                  width: PREVIEW_W,
                  height: PREVIEW_W * (cardH / cardW),
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  ref={cardRef}
                  style={{
                    width: cardW,
                    height: cardH,
                    transform: `scale(${PREVIEW_W / cardW})`,
                    transformOrigin: "top left",
                  }}
                >
                  <Template {...cardProps} />
                </div>
              </div>
              <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                {cardW}×{cardH}
              </div>
            </div>
            <div
              className="flex flex-col items-center gap-1"
              data-testid="story-card-preview-feed"
              aria-hidden={placeSize !== "feed"}
              style={placeSize === "feed" ? undefined : CARD_PARKED}
            >
              <div className="overline text-[10px]">Feed / Post · 4:5</div>
              <div
                style={{
                  width: PREVIEW_ALT_W,
                  height: PREVIEW_ALT_W * (cardAltH / cardAltW),
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  ref={cardAltRef}
                  style={{
                    width: cardAltW,
                    height: cardAltH,
                    transform: `scale(${PREVIEW_ALT_W / cardAltW})`,
                    transformOrigin: "top left",
                  }}
                >
                  <Template {...cardPropsAlt} />
                </div>
              </div>
              <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                {cardAltW}×{cardAltH}
              </div>
            </div>
          </div>

          <div
            className="mt-2 flex flex-col items-stretch gap-2"
            style={{ width: PREVIEW_W }}
          >
            <div className="flex flex-wrap items-center gap-2 justify-center">
              <button
                data-testid="story-card-download"
                onClick={download}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
              >
                {downloading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                PNG ({cardW}×{cardH})
              </button>
              <button
                data-testid="story-card-download-alt"
                onClick={downloadAlt}
                disabled={downloadingAlt}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--slate-900)] text-[var(--slate-900)] hover:bg-[var(--slate-900)] hover:text-white disabled:opacity-60"
              >
                {downloadingAlt ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                PNG ({cardAltW}×{cardAltH})
              </button>
            </div>
            {igPages.length > 0 && (
              <button
                data-testid="story-card-share-ig"
                onClick={() => setShareIgOpen((v) => !v)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-50 w-full"
              >
                <Instagram className="w-3.5 h-3.5" />
                {(() => {
                  const active =
                    igPages.find((p) => p.page_id === shareIgPageId) ||
                    igPages[0];
                  const handle = active?.ig_username
                    ? `@${active.ig_username}`
                    : "Instagram";
                  return `Share to ${handle}`;
                })()}
              </button>
            )}
            {fbPages.length > 0 && (
              <button
                data-testid="story-card-share-fb-story"
                onClick={() => setShareFbStoryOpen((v) => !v)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-blue-300 text-blue-800 hover:bg-blue-50 w-full"
              >
                <Facebook className="w-3.5 h-3.5" />
                Share to Facebook Story
              </button>
            )}
          </div>

          {shareIgOpen && igPages.length > 0 && (
            <div
              data-testid="story-card-ig-share-panel"
              className="mt-4 w-full max-w-sm border border-fuchsia-200 bg-fuchsia-50/40 p-3 text-left"
            >
              <div className="overline mb-1.5">Post type</div>
              <div className="flex gap-2 mb-3" role="tablist">
                <button
                  type="button"
                  data-testid="ig-target-feed"
                  onClick={() => setShareIgTarget("feed")}
                  className={
                    "flex-1 px-3 py-1.5 text-xs border " +
                    (shareIgTarget === "feed"
                      ? "bg-fuchsia-700 text-white border-fuchsia-700"
                      : "border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100")
                  }
                >
                  Feed post · {cardAltW}×{cardAltH}
                </button>
                <button
                  type="button"
                  data-testid="ig-target-story"
                  onClick={() => setShareIgTarget("story")}
                  className={
                    "flex-1 px-3 py-1.5 text-xs border " +
                    (shareIgTarget === "story"
                      ? "bg-fuchsia-700 text-white border-fuchsia-700"
                      : "border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100")
                  }
                >
                  Story · {cardW}×{cardH}
                </button>
              </div>
              <div className="overline mb-1.5">Instagram Business account</div>
              <select
                data-testid="ig-share-page-select"
                value={shareIgPageId}
                onChange={(e) => setShareIgPageId(e.target.value)}
                className="w-full mb-3 px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              >
                {igPages.map((p) => (
                  <option key={p.page_id} value={p.page_id}>
                    @{p.ig_username || p.ig_user_id} — via {p.page_name}
                  </option>
                ))}
              </select>
              <div className="overline mb-1.5">
                Caption
                {shareIgTarget === "story" && (
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-amber-700">
                    (Stories don&apos;t render captions — text is saved for
                    reference but not shown on the Story.)
                  </span>
                )}
              </div>
              <textarea
                data-testid="ig-share-caption"
                value={shareIgCaption}
                onChange={(e) => setShareIgCaption(e.target.value)}
                rows={shareIgTarget === "story" ? 3 : 6}
                placeholder={
                  shareIgTarget === "story"
                    ? "Optional note (not displayed on Story)…"
                    : "Instagram caption (LLM-authored copy pre-fills here)…"
                }
                className="w-full px-2 py-1.5 text-sm resize-y border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              />
              <div className="text-[11px] text-[var(--ink-muted)] mt-1">
                {shareIgCaption.length} chars · card is auto-converted
                from PNG to JPEG (Meta requirement).
                {shareIgTarget === "story" && " Publishes as a 24-hour Story."}
              </div>
              <ScheduleControls
                enabled={igScheduleEnabled}
                setEnabled={setIgScheduleEnabled}
                isoValue={igScheduledFor}
                setIsoValue={setIgScheduledFor}
              />
              <button
                data-testid="ig-share-publish"
                onClick={() => shareToIg.mutate()}
                disabled={
                  shareToIg.isPending ||
                  !shareIgPageId ||
                  // Stories don't need caption text; Feed still does.
                  (shareIgTarget === "feed" &&
                    shareIgCaption.trim().length === 0) ||
                  (igScheduleEnabled && !igScheduledFor)
                }
                className={
                  "mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white disabled:opacity-50 " +
                  (igScheduleEnabled
                    ? "bg-amber-700 hover:bg-amber-800"
                    : "bg-fuchsia-700 hover:bg-fuchsia-800")
                }
              >
                {shareToIg.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : igScheduleEnabled ? (
                  <Clock className="w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {shareToIg.isPending
                  ? igScheduleEnabled
                    ? "Scheduling…"
                    : "Publishing…"
                  : (() => {
                      const active =
                        igPages.find((p) => p.page_id === shareIgPageId) ||
                        igPages[0];
                      const handle = active?.ig_username
                        ? `@${active.ig_username}`
                        : "Instagram";
                      const suffix =
                        shareIgTarget === "story" ? " Story" : "";
                      return igScheduleEnabled
                        ? `Schedule ${handle}${suffix}`
                        : `Publish to ${handle}${suffix}`;
                    })()}
              </button>
            </div>
          )}

          {shareFbStoryOpen && fbPages.length > 0 && (
            <div
              data-testid="story-card-fb-story-panel"
              className="mt-4 w-full max-w-sm border border-blue-200 bg-blue-50/40 p-3 text-left"
            >
              <div className="overline mb-1.5">Facebook Page</div>
              <select
                data-testid="fb-story-page-select"
                value={shareFbStoryPageId}
                onChange={(e) => setShareFbStoryPageId(e.target.value)}
                className="w-full mb-3 px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              >
                {fbPages.map((p) => (
                  <option key={p.page_id} value={p.page_id}>
                    {p.page_name}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-[var(--ink-muted)] mb-1">
                Publishes the <strong>{cardW}×{cardH}</strong> (9:16) card
                as a Facebook Page Story. Stories don&apos;t support captions
                and expire from your Page 24 hours after publishing.
              </div>
              <ScheduleControls
                enabled={fbStoryScheduleEnabled}
                setEnabled={setFbStoryScheduleEnabled}
                isoValue={fbStoryScheduledFor}
                setIsoValue={setFbStoryScheduledFor}
              />
              <button
                data-testid="fb-story-publish"
                onClick={() => shareToFbStory.mutate()}
                disabled={
                  shareToFbStory.isPending ||
                  !shareFbStoryPageId ||
                  (fbStoryScheduleEnabled && !fbStoryScheduledFor)
                }
                className={
                  "mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white disabled:opacity-50 " +
                  (fbStoryScheduleEnabled
                    ? "bg-amber-700 hover:bg-amber-800"
                    : "bg-blue-700 hover:bg-blue-800")
                }
              >
                {shareToFbStory.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : fbStoryScheduleEnabled ? (
                  <Clock className="w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {shareToFbStory.isPending
                  ? fbStoryScheduleEnabled
                    ? "Scheduling…"
                    : "Publishing…"
                  : (() => {
                      const active =
                        fbPages.find(
                          (p) => p.page_id === shareFbStoryPageId,
                        ) || fbPages[0];
                      const name = active?.page_name || "Page";
                      return fbStoryScheduleEnabled
                        ? `Schedule ${name} Story`
                        : `Publish ${name} Story`;
                    })()}
              </button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="space-y-4 min-w-0">
          <div>
            <div className="overline mb-1.5">Design</div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`story-card-template-${t.id}`}
                  onClick={() => setTemplateId(t.id)}
                  className={`px-3 py-1.5 text-xs border ${
                    templateId === t.id
                      ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                      : "border-[var(--border)] hover:bg-[var(--secondary)]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-1.5">Size</div>
            <div className="flex flex-wrap gap-2">
              {[...CARD_SIZES].reverse().map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`story-card-photo-size-${c.id}`}
                  onClick={() => setPlaceSize(c.id)}
                  className={`px-3 py-1.5 text-xs border ${
                    placeSize === c.id
                      ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                      : "border-[var(--border)] hover:bg-[var(--secondary)]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[var(--ink-muted)] mt-1">
              Which card you see and position the photo on. Download and
              Share still offer both.
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="story-card-use-generic"
                checked={useGeneric}
                onChange={(e) => setUseGeneric(e.target.checked)}
              />
              Use generic background
            </label>
            {useGeneric && (
              <div className="mt-2">
                {backgrounds.length === 0 ? (
                  <p className="text-xs text-[var(--ink-muted)] italic">
                    No generic backgrounds yet.{" "}
                    <a
                      href="/settings"
                      className="text-[var(--accent)] underline"
                    >
                      Upload one in Settings → Story cards
                    </a>
                    .
                  </p>
                ) : (
                  <select
                    data-testid="story-card-bg-select"
                    value={selectedBgId || ""}
                    onChange={(e) => setSelectedBgId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
                  >
                    {backgrounds.map((b, i) => (
                      <option key={b.id} value={b.id}>
                        {b.label || `Background ${i + 1}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {bg && (
            <div data-testid="story-card-photo-position">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="overline">Photo position</span>
                <span className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                  {CARD_SIZES.find((c) => c.id === placeSize)?.label}
                </span>
              </div>
              {(() => {
                const isStory = placeSize === "story";
                const w = isStory ? cardW : cardAltW;
                const h = isStory ? cardH : cardAltH;
                const box = isStory ? storyBox : feedBox;
                const placement = isStory ? storyPlacement : feedPlacement;
                const props = isStory ? cardProps : cardPropsAlt;
                if (!bgAspect) {
                  return (
                    <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading photo…
                    </div>
                  );
                }
                const r = cardPhotoRect(placement, box, bgAspect);
                const margin = Math.round(w * 0.3);
                return (
                  <>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <button
                        type="button"
                        data-testid="story-card-photo-fit"
                        onClick={() => setPlacement(placeSize, { ...FIT_WIDTH })}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:bg-[var(--secondary)]"
                      >
                        <MoveHorizontal className="w-3 h-3" /> Fit to width
                      </button>
                      <button
                        type="button"
                        data-testid="story-card-photo-auto"
                        onClick={() => setPlacement(placeSize, null)}
                        disabled={!isManual(placement)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" /> Auto (fill)
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                    <PhotoStage
                      contentW={w}
                      contentH={h}
                      marginX={margin}
                      marginY={margin}
                      scale={280 / (w + 2 * margin)}
                      rect={{ left: r.left + box.left, top: r.top + box.top, width: r.width, height: r.height }}
                      src={bgReady}
                      unitW={box.width}
                      minW={box.width * MIN_ZOOM}
                      maxW={box.width * MAX_ZOOM}
                      viewports={[{
                        key: placeSize, left: 0, top: 0, width: w, height: h,
                        node: <Template {...props} />,
                      }]}
                      testid="story-card-photo-stage"
                      onChange={(nr) => setPlacement(placeSize, rectToPlacement(
                        { left: nr.left - box.left, top: nr.top - box.top, width: nr.width },
                        box.width,
                      ))}
                    />
                    </div>
                    <p className="text-[11px] text-[var(--ink-muted)] mt-1">
                      Drag a corner to resize (the shape stays), drag the photo to move it.
                      Story and Feed are placed separately, per design. Auto (fill) goes back
                      to the automatic crop.
                    </p>
                  </>
                );
              })()}
            </div>
          )}

          <div>
            <div className="overline mb-1.5">Headline (edit for the card)</div>
            <textarea
              data-testid="story-card-headline"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              rows={2}
              maxLength={140}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--ink)] bg-white"
            />
            <p className="text-[11px] text-[var(--ink-muted)] mt-1">
              Long headlines will wrap. If it looks awkward, shorten it or
              rewrite for the card — the article headline stays untouched.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-[var(--ink-muted)]">
            <button
              type="button"
              data-testid="story-card-shuffle"
              onClick={() => setShuffleNonce((n) => n + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)]"
            >
              <RefreshCw className="w-3 h-3" />
              Shuffle chip & tag
            </button>
            <span className="font-mono-display">
              chip: <strong>{chip || "—"}</strong> · tag:{" "}
              <strong>{tag || "—"}</strong>
            </span>
          </div>

          {!logo && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
              No logo set. Upload a PNG in{" "}
              <a href="/settings" className="underline">
                Settings → Story cards
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
